/**
 * 模組代碼: 1110-UI-01
 * 模組說明: 登入驗證功能頁面
 * @module modules/login/LoginPage
 */

// ----- import from npm modules -----
import { useNavigate } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import Cookies from 'js-cookie';

// ----- import base libraries -----
import Header from '../../features/main-frame/components/Header';
import Footer from '../../features/main-frame/components/Footer';
import {
    React,
    BaseComponent,
    connect,
} from '../../components/BaseComponent';
import PasswordInput from '../../components/PasswordInput';
import { MessageModal, MessageModalModeEnums } from '../../components/MessageModal';

// ----- import from local actions -----
import { login } from './../../redux/actions/AuthActions';
import { fetchUserPermissions } from './../../redux/actions/AccessActions';

// ----- contents -----
const FIXED_SALT = process.env.REACT_APP_FIXED_SALT;
const LOGIN_RETRY_LIMIT = 5;
const LOGIN_LOCK_MINUTES = 30;
const LOGIN_LOCK_KEY = 'loginLockUntil';
const LOGIN_FAIL_COUNT_KEY = 'loginFailCount';


class LoginPage extends BaseComponent {
    constructor(props) {
        super(props);
        this.state = {
            account: '',
            password: '',
            rememberAccount: false,
            alertMessage: '',
            message: { mode: MessageModalModeEnums.ERROR, title: '', body: ''},
        };
        this.setAccount = this.setAccount.bind(this);
        this.setPassword = this.setPassword.bind(this);
        this.setRememberAccount = this.setRememberAccount.bind(this);
        this.setAlertMessage = this.setAlertMessage.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.showAlert = this.showAlert.bind(this);
        this.messageModalRef = React.createRef();
        this.messageModal = null;
        this.toggleMessageModal = this.toggleMessageModal.bind(this);
    }

    setAccount(account) {
        this.setState({ account });
    }

    setPassword(password) {
        this.setState({ password });
    }

    setRememberAccount(rememberAccount) {
        this.setState({ rememberAccount });
    }

    setAlertMessage(alertMessage) {
        this.setState({ alertMessage });
    }

    showAlert(message) {
        this.setAlertMessage(message);
    }

    toggleMessageModal(isShow, mode = MessageModalModeEnums.ERROR, title = '', message = '請先登入') {
        if (this.messageModal) {
            if (isShow) {
                let body = message;
                const statusCode = message?.code || '';
                if (statusCode !== '') {
                    body = `[ ${statusCode} ] ${message.message}`;
                }
                this.setState({ message: { mode: mode, title: title, body: body }});
                this.messageModal.show();
            } else {
                this.messageModal.hide();
            }
        }
    }

    async handleSubmit(e) {
        e.preventDefault();
        e.stopPropagation();

        // 檢查是否鎖定
        const lockUntil = localStorage.getItem(LOGIN_LOCK_KEY);
        if (lockUntil) {
            if (Date.now() < Number(lockUntil)) {
                this.showAlert(`登入失敗次數過多，請於 ${LOGIN_LOCK_MINUTES} 分鐘後再試。`);
                form.classList.add('was-validated');
                return;
            } else {
                // 超過 30 分鐘，自動重設失敗次數與鎖定
                localStorage.removeItem(LOGIN_FAIL_COUNT_KEY);
                localStorage.removeItem(LOGIN_LOCK_KEY);
            }
        }

        const form = e.currentTarget;

        // 檢查表單是否驗證通過
        if (form.checkValidity() === false) {
            this.showAlert('[111000] 帳號密碼不得為空, ');
            form.classList.add('was-validated');
            return;
        }

        // 建立 FormData 讀取表单的值
        const formData = new FormData(form);
        const account = formData.get('loginAccount');
        const password = formData.get('loginPassword');
        const rememberAccount = this.state.rememberAccount;

        // 將密碼加密
        const encryptedPassword = CryptoJS.SHA256(password + FIXED_SALT).toString();
        formData.set('loginPassword', encryptedPassword);

        // 如果選擇記住帳號，則將帳號存入 localStorage
        const encryptedRemember = CryptoJS.AES.encrypt(account, FIXED_SALT).toString();
        if (rememberAccount) {
            Cookies.set('remember', encryptedRemember, { expires: 30, secure: true, sameSite: 'Strict' });
        } else {
            Cookies.remove('remember');
        }

        await this.props.onLogin(account, encryptedPassword)
            .then(() => {
                const status = this.props.auth.status;
                if (status === 'rejected') {
                    // 增加失敗次數
                    let failCount = Number(localStorage.getItem(LOGIN_FAIL_COUNT_KEY) || 0) + 1;
                    localStorage.setItem(LOGIN_FAIL_COUNT_KEY, failCount);

                    if (failCount >= LOGIN_RETRY_LIMIT) {
                        const lockUntil = Date.now() + LOGIN_LOCK_MINUTES * 60 * 1000;
                        localStorage.setItem(LOGIN_LOCK_KEY, lockUntil);
                        this.showAlert(`登入失敗次數過多，請於 ${LOGIN_LOCK_MINUTES} 分鐘後再試。`);
                    } else {
                        const error = `[${this.props.auth.statusCode}]  ${this.props.auth.error}, `;
                        this.showAlert(error + `（剩餘重試次數：${LOGIN_RETRY_LIMIT - failCount}）`);
                    }
                    return;
                }

                // 登入成功，清除失敗次數與鎖定
                localStorage.removeItem(LOGIN_FAIL_COUNT_KEY);
                localStorage.removeItem(LOGIN_LOCK_KEY);

                // 取得使用者權限
                this.props.fetchUserPermissions().then(() => {
                    // 如果登入成功，跳轉到 MainFrame 頁面
                    this.props.navigate('/module');
                    //window.location.href = '/module';
                });
            })
            .catch((error) => {
                console.error('login error:', error)
            });
    }

    componentDidMount() {
        // 讀取 Cookie 記錄的帳號
        const remember = Cookies.get('remember');
        if (remember !== undefined) {
            const decryptedRemember = CryptoJS.AES.decrypt(remember, FIXED_SALT).toString(CryptoJS.enc.Utf8);
            this.setState({ account: decryptedRemember, rememberAccount: true });
        }
        this.messageModal = new bootstrap.Modal(this.messageModalRef.current);
    }

    componentWillUnmount() {
        this.toggleMessageModal(false);
    }

    toggleLoginMessageModal = (isShow) => {
        this.toggleMessageModal(isShow, MessageModalModeEnums.INFO, '登入提示', '請先登入');
    }

    render() {
        return (
            <React.Fragment>
                <Header toggleLoginMessageModal={this.toggleLoginMessageModal} />
                <div className="header__Placeholder"></div>
                <div className="m__wrap">
                    <div className="m__contents">
                        <section id="login-container">
                            <form className="needs-validation d-flex flex-column gap-3" id="loginForm" onSubmit={this.handleSubmit}>
                                <h1 className="d-flex flex-column align-items-center justify-content-center">
                                    <figure className="w-75">
                                        <img src="/images/logo-LiftingYourLife.png" alt="Logo" />
                                    </figure>
                                    <div className="fs-5">台灣三菱電梯梯控管理系統後台</div>
                                </h1>
                                <div className="d-flex flex-column gap-3">
                                    <div>
                                        <label htmlFor="loginAccount" className="form-label">帳號</label>
                                        <input type="text" className="form-control" id="loginAccount" name="loginAccount" value={this.state.account} onChange={e => this.setAccount(e.target.value)} required />
                                    </div>
                                    <div>
                                        <label htmlFor="loginPassword" className="form-label">密碼</label>
                                        <PasswordInput
                                            id="loginPassword"
                                            name="loginPassword"
                                            value={this.state.password}
                                            onChange={e => this.setPassword(e.target.value)}
                                            validateType="login"
                                            placeholder=""
                                            defaultMassage="密碼不得為空白"
                                            size=""
                                            required
                                        />
                                    </div>
                                    <div>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox" value="" id="loginKeepAccount" name="loginKeepAccount" checked={this.state.rememberAccount} onChange={e => this.setRememberAccount(e.target.checked)} />
                                            <label className="form-check-label form-label" htmlFor="loginKeepAccount">記住我的帳號</label>
                                        </div>
                                    </div>
                                </div>
                                <button type="submit" className="btn btn-primary" id="signInButton">登入</button>
                                <div className={`global-invalid-feedback--login ${this.state.alertMessage ? 'd-block' : 'd-none'}`}>
                                    {this.state.alertMessage}
                                </div>
                            </form>
                        </section>
                    </div>
                </div>
                <Footer />
                <MessageModal
                    ref={this.messageModalRef}
                    mode={this.state.message.mode}
                    title={this.state.message.title}
                    message={this.state.message.body}
                />
            </React.Fragment>
        );
    }
}

function mapStateToProps(state) {
    return {
        auth: state.auth,
        account: state.account,
        password: state.password,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        onLogin: (account, password) => {
            return dispatch(login({ account: account, password: password }));
        },
        fetchUserPermissions: () => {
            return dispatch(fetchUserPermissions()).then(() => {
                return Promise.resolve();
            });
        },
    };
}

const ConnectedLoginPage = connect(mapStateToProps, mapDispatchToProps)(LoginPage);

export default function Login(props) {
    const navigate = useNavigate();
    return <ConnectedLoginPage {...props} navigate={navigate} />;
};
